import { getSupabaseClient } from '@/infrastructure/supabase/app'

const MAX_IMAGE_BYTES = 5 * 1024 * 1024
const allowedImageTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'])
const BUCKET = 'org-assets'

function sanitizeSegment(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'asset'
}

function extensionFor(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase()
  if (fromName && /^[a-z0-9]+$/.test(fromName)) return fromName
  if (file.type === 'image/jpeg') return 'jpg'
  if (file.type === 'image/png') return 'png'
  if (file.type === 'image/webp') return 'webp'
  if (file.type === 'image/gif') return 'gif'
  if (file.type === 'image/svg+xml') return 'svg'
  return 'img'
}

export async function uploadImageAsset(input: {
  readonly organizationId: string
  readonly scope: 'branding' | 'agreements' | 'profile'
  readonly ownerId: string
  readonly file: File
  readonly label?: string
  readonly maxBytes?: number
}): Promise<string> {
  const maxBytes = input.maxBytes ?? MAX_IMAGE_BYTES
  if (!allowedImageTypes.has(input.file.type)) {
    throw new Error('Formato de imagen no permitido. Usa JPG, PNG, WebP, GIF o SVG.')
  }
  if (input.file.size > maxBytes) {
    throw new Error(`La imagen supera el maximo permitido de ${Math.round(maxBytes / 1024 / 1024)} MB.`)
  }

  const safeLabel = sanitizeSegment(input.label ?? input.file.name)
  const ext = extensionFor(input.file)
  const objectPath = [
    'organizations',
    sanitizeSegment(input.organizationId),
    input.scope,
    sanitizeSegment(input.ownerId),
    `${Date.now()}-${crypto.randomUUID()}-${safeLabel}.${ext}`,
  ].join('/')

  const client = getSupabaseClient()
  const { error } = await client.storage.from(BUCKET).upload(objectPath, input.file, {
    contentType: input.file.type,
    upsert: false,
  })
  if (error) throw error

  const { data } = client.storage.from(BUCKET).getPublicUrl(objectPath)
  return data.publicUrl
}
